import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useParams } from 'react-router-dom';
import { neoApi, type ApiError } from '../api';
import { Page, Card, Button, Markdown, ChatTabs } from '../ui';

/**
 * Conversar com a IA do projeto (NEO Pilots → fluxo NÃO-código, roda no Hermes).
 * O agente enxerga os documentos do projeto (docs/*.md) como contexto. É consultivo:
 * orienta e sugere; ações que alteram documentos ficam pro board/Kanban.
 */

type Msg = { role: 'user' | 'assistant'; content: string };

const SUGESTOES = [
  'Resuma o posicionamento do projeto em uma frase.',
  'Que canais você priorizaria pro crescimento agora?',
  'Me dê 3 ideias de post pro Instagram na voz da marca.',
  'O que ainda falta definir na estratégia?',
];

export function NeoChat() {
  const { slug } = useParams<{ slug: string }>();
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  async function send(text: string) {
    const msg = text.trim();
    if (!msg || loading || !slug) return;
    setError(null);
    const history = messages.slice(-20);
    setMessages((m) => [...m, { role: 'user', content: msg }]);
    setInput('');
    setLoading(true);
    try {
      const { reply } = await neoApi.chat(slug, msg, history);
      setMessages((m) => [...m, { role: 'assistant', content: reply }]);
    } catch (e) {
      const err = e as ApiError;
      setError(
        err?.error === 'hermes_falhou'
          ? 'A IA não respondeu agora. Tente de novo em instantes.'
          : 'Não consegui enviar. Tente de novo.',
      );
    } finally {
      setLoading(false);
    }
  }

  function onKey(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void send(input);
    }
  }

  const empty = messages.length === 0;

  return (
    <Page title="Conversar com a IA" subtitle="Tire dúvidas e peça orientações sobre o projeto — a IA conhece os documentos do seu planejamento.">
      {slug && <ChatTabs slug={slug} />}
      <Card size="normal">
        <div style={{ display: 'flex', flexDirection: 'column', height: 'min(66vh, 620px)' }}>
          {/* histórico */}
          <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', paddingRight: 'var(--space-2)' }}>
            {empty && (
              <div style={{ margin: 'auto', textAlign: 'center', maxWidth: 460, color: 'var(--color-fg-muted)' }}>
                <div style={{ fontSize: 28, marginBottom: 'var(--space-2)' }}>💬</div>
                <p style={{ marginBottom: 'var(--space-4)' }}>
                  Converse com a IA sobre o projeto. Ela lê seu planejamento e responde com base nele.
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', justifyContent: 'center' }}>
                  {SUGESTOES.map((s) => (
                    <button
                      key={s}
                      onClick={() => void send(s)}
                      style={{
                        border: '1px solid var(--color-border)', borderRadius: 'var(--radius-pill, 999px)',
                        background: 'var(--color-bg-subtle)', color: 'var(--color-fg)', cursor: 'pointer',
                        padding: '6px 12px', fontSize: 'var(--font-size-sm)',
                      }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div
                key={i}
                style={{
                  alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '82%',
                  background: m.role === 'user' ? 'var(--color-accent)' : 'var(--color-bg-subtle)',
                  color: m.role === 'user' ? '#fff' : 'var(--color-fg)',
                  borderRadius: 'var(--radius-lg, 14px)',
                  padding: 'var(--space-3) var(--space-4)',
                }}
              >
                {m.role === 'user' ? (
                  <span style={{ whiteSpace: 'pre-wrap' }}>{m.content}</span>
                ) : (
                  <Markdown text={m.content} />
                )}
              </div>
            ))}

            {loading && (
              <div style={{ alignSelf: 'flex-start', color: 'var(--color-fg-muted)', fontSize: 'var(--font-size-sm)', padding: '0 var(--space-2)' }}>
                A IA está pensando…
              </div>
            )}
            <div ref={endRef} />
          </div>

          {error && (
            <div style={{ color: 'var(--color-danger, #dc2626)', fontSize: 'var(--font-size-sm)', margin: 'var(--space-2) 0' }}>{error}</div>
          )}

          {/* input */}
          <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'flex-end', marginTop: 'var(--space-3)', borderTop: '1px solid var(--color-border)', paddingTop: 'var(--space-3)' }}>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder="Escreva sua mensagem…  (Enter envia, Shift+Enter quebra linha)"
              rows={2}
              style={{
                flex: 1, resize: 'none', padding: 'var(--space-3)', borderRadius: 'var(--radius-md, 10px)',
                border: '1px solid var(--color-border)', background: 'var(--color-bg)', color: 'var(--color-fg)',
                font: 'inherit',
              }}
            />
            <Button onClick={() => void send(input)} disabled={loading || !input.trim()}>
              Enviar
            </Button>
          </div>
        </div>
      </Card>
    </Page>
  );
}
